import {
  InlineInputWrapper,
  type InlineInputWrapperProps,
} from '../inline-input-wrapper';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import {
  TagDropdown,
  type TagDropdownOption,
} from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import { useMemo } from 'react';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { stackedFieldTriggerClassName } from 'src/modules/shared/styles/stacked-field.styles';
import type {
  ComponentAppearance,
  ComponentSize,
} from 'src/modules/shared/types/component.type';

type OptionValue = string;

interface Option {
  label: string;
  value: OptionValue;
}

interface InlineSelectInputProps
  extends Omit<InlineInputWrapperProps<OptionValue>, 'children'> {
  options: Option[];
  placeholder?: string;
  loading?: boolean;
  appearance?: ComponentAppearance;
}

export const InlineSelectInput: React.FC<InlineSelectInputProps> = ({
  options,
  placeholder,
  loading = false,
  size = 'sm',
  variant = 'primary',
  appearance = 'outline',
  layout = 'inline',
  disableCopy,
  ...props
}) => {
  const { t } = useI18n();
  const isStacked = layout === 'stacked';
  const compactInlineSizeMap: Record<ComponentSize, ComponentSize> = {
    '2xs': '2xs',
    xs: '2xs',
    sm: 'xs',
    md: 'sm',
    lg: 'md',
  };
  const inlineTriggerSize = isStacked ? size : compactInlineSizeMap[size];
  const dropdownOptions: TagDropdownOption[] = useMemo(() => {
    return options.map((option) => ({
      id: String(option.value),
      content: option.label,
    }));
  }, [options]);

  const getSelectedOptionLabel = (value: OptionValue) => {
    const selectedOption = options.find((option) => option.value === value);
    if (!selectedOption) {
      return placeholder ?? t('shared.inline.select');
    }

    return selectedOption.label;
  };

  const handleOptionSelect = (
    option: TagDropdownOption,
    onChange: (value: OptionValue) => void
  ) => {
    const selectedOptionId = String(option.id);

    const selectedOption = options.find(
      (option) => String(option.value) === selectedOptionId
    );
    if (!selectedOption) return;

    onChange(selectedOption.value);
  };

  return (
    <InlineInputWrapper<OptionValue>
      {...props}
      size={size}
      layout={layout}
      disableCopy={disableCopy ?? isStacked}
    >
      {({ value, onChange, disabled }) =>
        isStacked ? (
          <MenuDropdown
            label={getSelectedOptionLabel(value)}
            items={dropdownOptions.map((option) => ({
              ...option,
              onClick: () => handleOptionSelect(option, onChange),
            }))}
            className={`stacked-field-select-trigger w-full justify-between ${stackedFieldTriggerClassName}`}
            handlerContainerClassName="w-full"
            variant={variant}
            appearance={appearance}
            size={size}
            isLoading={loading}
            disabled={disabled}
          />
        ) : (
          <TagDropdown
            content={getSelectedOptionLabel(value)}
            variant={variant}
            size={inlineTriggerSize}
            appearance={appearance}
            isLoading={loading}
            disabled={disabled}
            options={dropdownOptions}
            onOptionSelect={(option: TagDropdownOption) => {
              handleOptionSelect(option, onChange);
            }}
            className="max-w-[180px] transition-all"
          />
        )
      }
    </InlineInputWrapper>
  );
};
